import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiException } from "@/lib/api";
import { useAuthStore, type CurrentUser } from "@/store/authStore";
import type {
  LoginInput,
  RegisterInput,
  ForgotPasswordInput,
  ResetPasswordInput,
  UpdateProfileInput,
  ChangePasswordInput,
} from "@mersa/shared";

export const useLogin = () => {
  const queryClient = useQueryClient();
  const setToken = useAuthStore((s) => s.setToken);
  return useMutation({
    mutationFn: async (input: LoginInput) => {
      const data = await api.post<{ accessToken: string }>("/api/auth/login", input);
      setToken(data.accessToken);
      // Misafir sepetini kullanıcıya birleştir (fire-and-forget hata olursa yoksay)
      try {
        await api.post("/api/cart/merge", undefined, { auth: true });
      } catch {
        /* ignore */
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
      queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
  });
};

export const useRegister = () =>
  useMutation({
    mutationFn: (input: RegisterInput) =>
      api.post<{ id: string; email: string }>("/api/auth/register", input),
  });

export const useForgotPassword = () =>
  useMutation({
    mutationFn: (input: ForgotPasswordInput) =>
      api.post<{ success: boolean }>("/api/auth/forgot-password", input),
  });

export const useResetPassword = () =>
  useMutation({
    mutationFn: ({ token, password }: ResetPasswordInput & { token: string }) =>
      api.post<{ success: boolean }>(`/api/auth/reset-password/${token}`, { password }),
  });

export const useLogout = () => {
  const queryClient = useQueryClient();
  const clear = useAuthStore((s) => s.clear);
  return useMutation({
    mutationFn: () => api.post<{ success: boolean }>("/api/auth/logout"),
    onSuccess: () => {
      clear();
      queryClient.clear();
    },
  });
};

export const useMe = () => {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setUser = useAuthStore((s) => s.setUser);
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const data = await api.get<CurrentUser>("/api/auth/me", { auth: true });
      setUser(data);
      return data;
    },
    enabled: !!accessToken,
    retry: (failureCount, error) => {
      if (error instanceof ApiException && error.status === 401) return false;
      return failureCount < 2;
    },
  });
};

export const useUpdateMe = () => {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: (input: UpdateProfileInput) =>
      api.patch<CurrentUser>("/api/auth/me", input, { auth: true }),
    onSuccess: (data) => {
      setUser(data);
      queryClient.setQueryData(["me"], data);
    },
  });
};

export const useChangePassword = () =>
  useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api.post<{ success: boolean }>("/api/auth/me/change-password", input, { auth: true }),
  });
